import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import isNil from 'lodash/isNil';
import Ripple from '../Ripple';
import SafeAnchor from '../SafeAnchor';
import { shallowEqual, useClassNames } from '../utils';
import { SidenavContext } from '../Sidenav/Sidenav';
import NavContext from './NavContext';
import { NavbarContext } from '../Navbar/Navbar';
import SidenavItem from '../Sidenav/SidenavItem';
import NavbarItem from '../Navbar/NavbarItem';

/**
 * The <Nav.Item> API
 */
var NavItem = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _props$as = props.as,
      Component = _props$as === void 0 ? SafeAnchor : _props$as,
      activeProp = props.active,
      disabled = props.disabled,
      eventKey = props.eventKey,
      className = props.className,
      _props$classPrefix = props.classPrefix,
      classPrefix = _props$classPrefix === void 0 ? 'nav-item' : _props$classPrefix,
      style = props.style,
      children = props.children,
      icon = props.icon,
      divider = props.divider,
      panel = props.panel,
      onClick = props.onClick,
      onSelectProp = props.onSelect,
      rest = _objectWithoutPropertiesLoose(props, ["as", "active", "disabled", "eventKey", "className", "classPrefix", "style", "children", "icon", "divider", "panel", "onClick", "onSelect"]);

  var _useContext = useContext(NavContext),
      activeKey = _useContext.activeKey,
      onSelectFromNav = _useContext.onSelect;

  var active = activeProp !== null && activeProp !== void 0 ? activeProp : !isNil(eventKey) && shallowEqual(eventKey, activeKey);
  var emitSelect = useCallback(function (event) {
    onSelectProp === null || onSelectProp === void 0 ? void 0 : onSelectProp(eventKey, event);
    onSelectFromNav === null || onSelectFromNav === void 0 ? void 0 : onSelectFromNav(eventKey, event);
  }, [eventKey, onSelectProp, onSelectFromNav]);
  var navbar = useContext(NavbarContext);
  var sidenav = useContext(SidenavContext);

  var _useClassNames = useClassNames(classPrefix),
      withClassPrefix = _useClassNames.withClassPrefix,
      merge = _useClassNames.merge,
      prefix = _useClassNames.prefix;

  var classes = merge(className, withClassPrefix({
    active: active,
    disabled: disabled
  }));
  var handleClick = useCallback(function (event) {
    if (!disabled) {
      emitSelect(event);
      onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }
  }, [disabled, emitSelect, onClick]);

  if (sidenav) {
    return /*#__PURE__*/React.createElement(SidenavItem, props);
  }

  if (divider) {
    return /*#__PURE__*/React.createElement("div", _extends({
      ref: ref,
      role: "separator",
      style: style,
      className: merge(className, prefix('divider'))
    }, rest));
  }

  if (panel) {
    return /*#__PURE__*/React.createElement("div", _extends({
      ref: ref,
      style: style,
      className: merge(className, prefix('panel'))
    }, rest), children);
  }

  if (navbar) {
    return /*#__PURE__*/React.createElement(NavbarItem, _extends({
      ref: ref
    }, props));
  }

  return /*#__PURE__*/React.createElement(Component, _extends({
    ref: ref,
    tabIndex: disabled ? -1 : undefined
  }, rest, {
    className: classes,
    onClick: handleClick,
    style: style,
    "aria-selected": active || undefined
  }), icon, children, /*#__PURE__*/React.createElement(Ripple, null));
});
NavItem.displayName = 'Nav.Item';
NavItem.propTypes = {
  as: PropTypes.elementType,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  classPrefix: PropTypes.string,
  divider: PropTypes.bool,
  panel: PropTypes.bool,
  onClick: PropTypes.func,
  style: PropTypes.object,
  icon: PropTypes.node,
  onSelect: PropTypes.func,
  children: PropTypes.node,
  eventKey: PropTypes.any
};
export default NavItem;